import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, useAcao, type TomEtiqueta } from '../comum';

/**
 * Monitores: condições que o cliente define e o produto vigia.
 *
 * 🔴 **Monitor não é Sentinela, e a tela precisa deixar isso claro.** A regra do
 * Sentinela é política de conformidade — ela diz o que o parque *deve* ser. O
 * monitor é uma condição operacional que alguém quis acompanhar, e que dispara
 * quando cruza um limite. Confundir os dois faz a pessoa criar regra onde queria
 * alerta, e alerta onde queria política.
 *
 * 🔴 **`UNKNOWN` é um estado, não um erro.** Um monitor de espaço em disco num
 * parque sem a Custom Inventory Rule correspondente não está OK e não está
 * disparado: ele não tem o dado. Mostrar "OK" nesse caso é a mentira mais fácil
 * desta tela.
 */

interface Monitor {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string;
  readonly condicao: { readonly kind: string; readonly threshold?: number };
  readonly frequencia: string;
  readonly ativo: boolean;
  readonly dono: string;
  readonly criadoEm: string;
}

interface Avaliacao {
  readonly estado: 'OK' | 'TRIGGERED' | 'UNKNOWN' | 'ERROR';
  readonly afetados: number | null;
  readonly avaliadosEm: string;
}

interface Historico {
  readonly itens: readonly Avaliacao[];
  readonly tendencia: 'UP' | 'DOWN' | 'STABLE' | 'UNKNOWN';
}

const ESTADO: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta; explica: string }>> = {
  OK: { rotulo: 'Dentro do limite', tom: 'ok', explica: 'A condição foi avaliada e não disparou.' },
  TRIGGERED: {
    rotulo: 'Disparado',
    tom: 'erro',
    explica: 'A condição foi cruzada. O evento saiu para os destinos configurados.',
  },
  UNKNOWN: {
    rotulo: 'Sem dado',
    tom: 'alerta',
    explica:
      'O produto não tem o dado para avaliar esta condição — disco livre, por exemplo, ' +
      'depende de uma Custom Inventory Rule no KACE. Não é OK e não é disparo.',
  },
  ERROR: { rotulo: 'Erro', tom: 'erro', explica: 'A avaliação falhou.' },
};

const TENDENCIA: Readonly<Record<string, string>> = {
  UP: '▲ piorando',
  DOWN: '▼ melhorando',
  STABLE: '= estável',
  UNKNOWN: '– sem histórico',
};

/**
 * As condições que o produto sabe avaliar.
 *
 * 🔴 Catálogo fechado, e cada uma diz de qual dado depende. A tela anterior
 * oferecia só "sem agente" num botão solto, e as outras duas condições
 * existiam no domínio sem caminho de criação.
 */
const CONDICOES = [
  {
    kind: 'missing_agent',
    rotulo: 'Máquinas sem reportar inventário',
    depende: 'inventário sincronizado — sempre disponível',
  },
  {
    kind: 'critical_patches',
    rotulo: 'Máquinas com patch crítico pendente',
    depende: 'contadores de patch por máquina — vêm no sync',
  },
  {
    kind: 'disk_free_percent',
    rotulo: 'Máquinas com pouco espaço livre',
    depende: 'Custom Inventory Rule de disco — se não existir, o monitor fica "sem dado"',
  },
] as const;

export function Monitores({
  aoPerderSessao,
  podeCriar,
}: {
  aoPerderSessao: () => void;
  podeCriar: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [itens, setItens] = useState<readonly Monitor[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [historico, setHistorico] = useState<Record<string, Historico>>({});

  const [nome, setNome] = useState('');
  const [condicao, setCondicao] = useState<string>('missing_agent');
  const [limite, setLimite] = useState(10);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const r = await api.get<{ itens: readonly Monitor[] }>('/api/monitores');
      setItens(r.itens);
      setErro(null);
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
      else setErro(e instanceof Error ? e.message : String(e));
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const verHistorico = useCallback(
    async (id: string): Promise<void> => {
      try {
        const h = await api.get<Historico>(`/api/monitores/${encodeURIComponent(id)}/avaliacoes`);
        setHistorico((x) => ({ ...x, [id]: h }));
      } catch {
        // Histórico é complemento: falhar aqui não pode apagar a lista que já
        // está na tela. O cartão simplesmente não mostra o último estado.
      }
    },
    [],
  );

  const criar = (): void => {
    if (nome.trim() === '') {
      acao.falhar('Dê um nome ao monitor — é por ele que o alerta será reconhecido.');
      return;
    }
    void acao.rodar(async () => {
      await api.post('/api/monitores', {
        name: nome.trim(),
        description: CONDICOES.find((c) => c.kind === condicao)?.rotulo ?? '',
        condition:
          condicao === 'disk_free_percent'
            ? { kind: 'disk_free_percent', operator: 'lt', threshold: limite }
            : { kind: condicao },
        frequency: 'daily',
      });
      setNome('');
      await carregar();
      return 'Monitor criado. A avaliação roda pela fila de trabalho.';
    });
  };

  const alternar = (m: Monitor): void => {
    void acao.rodar(async () => {
      await api.post(
        `/api/monitores/${encodeURIComponent(m.id)}/${m.ativo ? 'desativar' : 'ativar'}`,
        {},
      );
      await carregar();
      return m.ativo ? 'Monitor desativado.' : 'Monitor ativado.';
    });
  };

  const avaliarAgora = (m: Monitor): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ estado: string; afetados: number | null; ignorado?: boolean }>(
        `/api/monitores/${encodeURIComponent(m.id)}/avaliar`,
        {},
      );
      await verHistorico(m.id);
      await carregar();
      if (r.ignorado === true) return 'Monitor inativo — nada foi avaliado.';
      return `Avaliado: ${ESTADO[r.estado]?.rotulo ?? r.estado}${
        r.afetados === null ? '' : ` · ${r.afetados} máquina(s)`
      }.`;
    });
  };

  return (
    <div className="tela">
      <div className="cabecalho-tela">
        <h1>Monitores</h1>
        <p>
          Condições que você define e o produto vigia sozinho, sobre o inventário já
          sincronizado.
        </p>
      </div>

      <Aviso tipo="info">
        <strong>Monitor não é regra do Sentinela.</strong> A regra diz o que o parque{' '}
        <em>deve</em> ser — é política de conformidade. O monitor acompanha uma condição
        operacional e avisa quando ela cruza um limite. Os dois convivem.
      </Aviso>

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}
      {erro !== null && <Aviso tipo="erro">{erro}</Aviso>}

      {podeCriar && (
        <Painel titulo="Novo monitor">
          <div className="grade2">
            <label>
              <span className="suave">Nome</span>
              <input
                value={nome}
                placeholder="Ex.: Máquinas paradas do Back Office"
                onChange={(e) => setNome(e.target.value)}
              />
            </label>
            <label>
              <span className="suave">Condição</span>
              <select value={condicao} onChange={(e) => setCondicao(e.target.value)}>
                {CONDICOES.map((c) => (
                  <option key={c.kind} value={c.kind}>
                    {c.rotulo}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {condicao === 'disk_free_percent' && (
            <label>
              <span className="suave">Disparar abaixo de (% livre)</span>
              <input
                type="number"
                min={1}
                max={99}
                value={limite}
                onChange={(e) => setLimite(Number(e.target.value))}
              />
            </label>
          )}

          {/*
            🔴 A dependência de dado fica visível ANTES de criar.
            Um monitor de disco num parque sem a CIR correspondente nasce em
            "sem dado" e fica lá — e quem criou não teria como saber por quê.
          */}
          <p className="suave">
            Depende de: {CONDICOES.find((c) => c.kind === condicao)?.depende}
          </p>

          <button className="primario encolhe" disabled={acao.executando} onClick={criar}>
            Criar monitor
          </button>
        </Painel>
      )}

      <Painel
        titulo={
          itens === null || itens.length === 0
            ? 'Monitores'
            : `Monitores — ${itens.filter((m) => m.ativo).length} ativo(s)`
        }
      >
        {itens === null && <p className="suave">Carregando monitores…</p>}

        {itens !== null && itens.length === 0 && (
          <p className="suave">
            Nenhum monitor criado.{' '}
            {podeCriar
              ? 'Crie o primeiro acima — o produto passa a avisar sem você precisar perguntar.'
              : 'Criar monitor exige permissão de escrita no inventário.'}
          </p>
        )}

        {(itens ?? []).map((m) => {
          const h = historico[m.id];
          const ultima = h?.itens[h.itens.length - 1];
          const estado = ultima === undefined ? undefined : ESTADO[ultima.estado];
          return (
            <div
              key={m.id}
              style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 14, marginBottom: 14 }}
            >
              <div className="linha">
                <div>
                  <strong>{m.nome}</strong>{' '}
                  <span className="suave">
                    · {m.frequencia} · criado por {m.dono} em {data(m.criadoEm)}
                  </span>
                </div>
                <Etiqueta tom={m.ativo ? 'ok' : 'neutro'}>
                  {m.ativo ? 'Ativo' : 'Inativo'}
                </Etiqueta>
              </div>

              {m.descricao !== '' && <p className="suave">{m.descricao}</p>}

              {estado !== undefined && ultima !== undefined ? (
                <p className="suave">
                  <Etiqueta tom={estado.tom} titulo={estado.explica}>
                    {estado.rotulo}
                  </Etiqueta>{' '}
                  {ultima.afetados !== null && `${ultima.afetados} máquina(s) · `}
                  {TENDENCIA[h?.tendencia ?? 'UNKNOWN']} · {data(ultima.avaliadosEm)}
                </p>
              ) : (
                <p className="suave">
                  Sem avaliação carregada.{' '}
                  <button className="link" onClick={() => void verHistorico(m.id)}>
                    Ver histórico
                  </button>
                </p>
              )}

              {estado !== undefined && <p className="suave">{estado.explica}</p>}

              <div className="linha">
                <button className="encolhe" disabled={acao.executando} onClick={() => avaliarAgora(m)}>
                  Avaliar agora
                </button>
                {podeCriar && (
                  <button className="encolhe" disabled={acao.executando} onClick={() => alternar(m)}>
                    {m.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </Painel>
    </div>
  );
}
